<script lang="ts">
  import type { AIProvider } from '$lib/stores/settings'

  // Import provider icons
  import openaiIcon from '$lib/../assets/ai-model-icons/openai.png'
  import claudeIcon from '$lib/../assets/ai-model-icons/claude.png'
  import googleIcon from '$lib/../assets/ai-model-icons/google.png'
  import upstageIcon from '$lib/../assets/ai-model-icons/upstage.png'
  
  interface Props {
    provider: AIProvider
    showApiKey?: boolean
    onToggle: () => void
    onUpdateApiKey: (value: string) => void
    onToggleApiKeyVisibility: () => void
  }

  let {
    provider,
    showApiKey = false,
    onToggle,
    onUpdateApiKey,
    onToggleApiKeyVisibility
  }: Props = $props()
  
  
  // Map provider IDs to imported icons
  const providerIcons: Record<string, string> = {
    openai: openaiIcon,
    anthropic: claudeIcon,
    google: googleIcon,
    upstage: upstageIcon
  }
  
  // Get the correct icon for the provider
  const providerIcon = $derived(providerIcons[provider.id] || provider.icon)
</script>

<div class="provider-card {provider.enabled ? 'enabled' : ''}">
  <div class="provider-header">
    <div class="provider-info">
      {#if providerIcon && providerIcon.includes('/')}
        <img src={providerIcon} alt="{provider.name} icon" class="provider-icon-img" />
      {:else}
        <span class="provider-icon">{provider.icon}</span>
      {/if}
      <h4 class="provider-name">
        {provider.name}
      </h4>
    </div>
    <label class="toggle-wrapper">
      <input
        type="checkbox"
        class="toggle-input"
        checked={provider.enabled}
        onchange={onToggle}
      />
      <span class="toggle-switch"></span>
    </label>
  </div>
  
  {#if provider.enabled}
    <div class="provider-config">
      <div class="form-group">
        <label for="{provider.id}-key">API 키</label>
        <div class="input-group">
          <div class="input-with-icon">
            <input
              id="{provider.id}-key"
              type={showApiKey ? "text" : "password"}
              value={provider.apiKey}
              oninput={(e) => onUpdateApiKey(e.currentTarget.value)}
              placeholder="{provider.name} API 키를 입력하세요"
              class="input"
            />
            <button
              type="button"
              class="eye-toggle"
              onclick={onToggleApiKeyVisibility}
              title={showApiKey ? "숨기기" : "보기"}
            >
              {#if showApiKey}
                <!-- Eye open icon -->
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              {:else}
                <!-- Eye closed icon -->
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              {/if}
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  /* Provider Card - Simplified design */
  .provider-card {
    padding: 20px 0;
    border-bottom: 1px solid var(--border-primary);
    transition: all 0.2s;
  }
  
  .provider-card:last-child {
    border-bottom: none;
  }
  
  .provider-card.enabled {
    /* Remove box shadow, keep subtle visual feedback */
  }
  
  .provider-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  
  .provider-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .provider-icon {
    font-size: 24px;
  }
  
  .provider-icon-img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
  
  /* Dark mode filter for provider icons */
  :global(.dark-theme) .provider-icon-img {
    filter: brightness(0) invert(1);
  }

  /* Auto mode: follow system preference */
  @media (prefers-color-scheme: dark) {
    :global(:root:not(.light-theme):not(.dark-theme)) .provider-icon-img,
    :global(:root.auto-theme) .provider-icon-img {
      filter: brightness(0) invert(1);
    }
  }

  /* Ensure light theme always shows original colors */
  :global(.light-theme) .provider-icon-img {
    filter: none;
  }
  
  .provider-name {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }
  
  /* Toggle Switch - Original styles */
  .toggle-wrapper {
    position: relative;
    display: inline-block;
  }
  
  .toggle-input {
    display: none;
  }
  
  .toggle-switch {
    display: block;
    width: 48px;
    height: 24px;
    background: var(--border-primary);
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    transition: background 0.2s;
  }
  
  .toggle-switch::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
  }
  
  .toggle-input:checked + .toggle-switch {
    background: var(--accent-primary);
  }
  
  .toggle-input:checked + .toggle-switch::after {
    transform: translateX(24px);
  }
  
  .provider-config {
    padding-top: 16px;
    border-top: 1px solid var(--bg-tertiary);
  }
  
  /* Form Styles - Original */
  .form-group {
    margin-bottom: 16px;
  }
  
  .form-group:last-child {
    margin-bottom: 0;
  }
  
  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: 6px;
  }
  
  .input-group {
    display: flex;
    gap: 8px;
  }
  
  .input-with-icon {
    position: relative;
    flex: 1;
    display: flex;
  }
  
  .input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--border-secondary);
    border-radius: 6px;
    font-size: 14px;
    background: var(--bg-primary);
    color: var(--text-primary);
    transition: all 0.2s;
  }
  
  .input-with-icon .input {
    padding-right: 40px;
  }
  
  .input:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
  }
  
  .eye-toggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
  }
  
  .eye-toggle:hover {
    color: var(--text-primary);
  }
  
  /* Dark mode handled by CSS variables */
</style>